// ============================================================
// Naija Explorer — Phase 2 TypeScript Types
// ============================================================

export type UserRole = 'parent' | 'child' | 'admin';
export type AgeGroup = '6-8' | '9-11' | '12-14';

// Matches public.user_profiles table
export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Matches public.child_profiles table
export interface ChildProfile {
  id: string;
  parentId: string;
  displayName: string;
  avatar: string;
  ageGroup: AgeGroup;
  createdAt: string;
  updatedAt: string;
}

// DB row types (snake_case — used in service layer conversions)
export interface UserProfileRow {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChildProfileRow {
  id: string;
  parent_id: string;
  display_name: string;
  avatar: string;
  age_group: AgeGroup;
  created_at: string;
  updated_at: string;
}

// Auth context value shape
export interface AuthContextValue {
  user: any | null;
  session: any | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// Child session context — tracks the currently active child
export interface ChildSessionContext {
  selectedChild: ChildProfile | null;
  setSelectedChild: (child: ChildProfile | null) => void;
  clearChildSession: () => void;
}

// Avatar options for child profiles
export const CHILD_AVATARS = [
  { id: 'explorer-1', emoji: '🧒', label: 'Explorer' },
  { id: 'explorer-2', emoji: '👧', label: 'Adventurer' },
  { id: 'explorer-3', emoji: '👦', label: 'Discoverer' },
  { id: 'explorer-4', emoji: '🧑', label: 'Scholar' },
  { id: 'explorer-5', emoji: '👩', label: 'Learner' },
  { id: 'explorer-6', emoji: '🧑‍🎓', label: 'Graduate' },
] as const;

export type AvatarId = typeof CHILD_AVATARS[number]['id'];

export function getAvatarEmoji(avatarId: string): string {
  return CHILD_AVATARS.find((a) => a.id === avatarId)?.emoji ?? '🧒';
}

export function getAvatarLabel(avatarId: string): string {
  return CHILD_AVATARS.find((a) => a.id === avatarId)?.label ?? 'Explorer';
}

// Converters: DB row → app type
export function toUserProfile(row: UserProfileRow): UserProfile {
  return {
    id: row.id,
    email: row.email,
    fullName: row.full_name,
    role: row.role,
    avatarUrl: row.avatar_url,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toChildProfile(row: ChildProfileRow): ChildProfile {
  return {
    id: row.id,
    parentId: row.parent_id,
    displayName: row.display_name,
    avatar: row.avatar,
    ageGroup: row.age_group,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
